import type { RegionSnapshot } from '@wildfire/core';

const BASE = import.meta.env.VITE_API_BASE ?? '';

export interface Envelope<T> {
  ok: boolean;
  data: T;
  meta?: {
    count?: number;
    cached: boolean;
    cacheAge: number;
    generatedAt: string;
    sources: string[];
    warnings?: Array<{ source: string; message: string }>;
  };
  error?: { code: string; message: string; source?: string };
}

async function getJson<T>(path: string): Promise<Envelope<T>> {
  const url = `${BASE}${path}`;
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    throw new Error(
      `Could not reach ${url} — ${(err as Error).message}. ` +
        `Check that VITE_API_BASE points at your API (e.g. Railway or Worker URL) and that the API is reachable from this browser.`,
    );
  }

  // Surface HTML / non-JSON responses with a useful message instead of
  // letting `res.json()` throw the cryptic WebKit "string did not match
  // the expected pattern" error. The most common cause is VITE_API_BASE
  // being unset at build time, so fetches land on the Pages origin and
  // get back an HTML 404.
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    const preview = (await res.text()).slice(0, 120).replace(/\s+/g, ' ').trim();
    const hint = BASE
      ? `VITE_API_BASE is "${BASE}"; confirm it returns JSON.`
      : 'VITE_API_BASE is empty — set it to your API URL in Pages env vars and redeploy.';
    throw new Error(
      `Expected JSON from ${url}, got ${res.status} ${ct || 'no content-type'}. ${hint} Response preview: ${preview}`,
    );
  }

  let body: Envelope<T>;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch (err) {
    throw new Error(`Failed to parse JSON from ${url}: ${(err as Error).message}`);
  }
  if (!body.ok) {
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
  return body;
}

export async function fetchSnapshot(region: string): Promise<RegionSnapshot> {
  const body = await getJson<RegionSnapshot>(`/api/snapshot/${region}`);
  return body.data;
}

export async function fetchRegions(): Promise<Record<string, { name: string; states: string[]; bbox: [number, number, number, number] }>> {
  const body = await getJson<Record<string, { name: string; states: string[]; bbox: [number, number, number, number] }>>('/api/regions');
  return body.data;
}
